/** The content frame every screen sits in.
 *
 * The design gives each screen its own measure — 1100 for sign-in and the
 * profile, 1200 for tokens, 1300 for tracks and admin, 1400 for sessions —
 * but the same gutter and the same rhythm under the header, so the width is a
 * prop and the padding is not. Keeping it here rather than in `Shell` means a
 * screen decides its own measure without the shell having to know which
 * screen is routed.
 */

import type { CSSProperties, ReactNode } from 'react';

export interface PageProps {
  children: ReactNode;
  /** Content measure in pixels. */
  max?: number;
  /** Extra layout utilities for the `<main>` itself. */
  className?: string;
  /** Names the `main` landmark when the screen's own h1 is not enough. */
  label?: string;
  /** Centres the content in the viewport — the sign-in screen only. */
  center?: boolean;
  style?: CSSProperties;
}

export function Page({ children, max = 1300, className, label, center, style }: PageProps) {
  return (
    <main
      aria-label={label}
      className={className}
      style={{
        width: '100%',
        maxWidth: max,
        margin: '0 auto',
        padding: center ? '48px 20px' : '22px 20px 40px',
        ...(center ? { flex: 1, display: 'grid', alignItems: 'center' } : null),
        ...style,
      }}
    >
      {children}
    </main>
  );
}

export default Page;
